// Navigation shim — Phase G1.
//
// Translates the legacy `setScreen("home")` API used throughout
// App.jsx + screen components into TanStack Router URLs. Lets G1
// land routing without rewriting every call site.
//
// Phase G3 cleanup will progressively replace `setScreen(...)` call
// sites with direct `router.navigate({ to: '/...' })` or `<Link to>`
// usage. Until then, this map is the single source of truth.
//
// Routes that don't appear here are gate-rendered (LoginScreen,
// WhosPlayingScreen, OnboardingScreen at first run) — those screens
// have no URL because they're conditional on app state, not URL.

// Modern kid-session routes (Phase G3+). Distinct from SCREEN_TO_PATH's
// legacy `session: "/session"` entry — these are the real URLs the four
// session screens mount on. Single source of truth for any gate that
// needs to know "is the kid mid-session right now" (e.g. mascotGate.js's
// no-hijack exclusion).
export const SESSION_PATHS = new Set([
  "/play/session",
  "/play/reading",
  "/play/mixed",
  "/play/foundation",
]);

const SCREEN_TO_PATH = {
  home: "/",
  concept: "/concept",
  session: "/session",
  done: "/done",
  dashboard: "/dashboard",
  settings: "/settings",
  "mascot-pick": "/mascot-pick",
  path: "/path",
  profiles: "/profiles",
  library: "/library",
  story: "/story",
};

const PATH_TO_SCREEN = Object.fromEntries(
  Object.entries(SCREEN_TO_PATH).map(([k, v]) => [v, k]),
);

/** Create the legacy setScreen(name) shim backed by router.navigate.
 * Accepts the TanStack Router instance and returns a function with
 * the legacy signature. Unknown screen names log a warning and
 * navigate to "/" as a safe fallback.
 *
 * Preserves search params across every navigation. Critical for
 * `?dev=1` (useAuth dev bypass), Supabase OAuth callbacks, and any
 * future query-string-driven flags. Without `search: prev => prev`,
 * TanStack Router drops query params on navigate, which broke e2e
 * tests that rely on `?dev=1` persistence (caught during G1
 * verification). */
export function makeSetScreen(router) {
  return function setScreen(screenName) {
    const to = SCREEN_TO_PATH[screenName];
    if (!to) {
      console.warn(
        `[navigation] unknown screen "${screenName}" — falling back to "/"`,
      );
      router.navigate({ to: "/", search: (prev) => prev });
      return;
    }
    router.navigate({ to, search: (prev) => prev });
  };
}

/** Inverse: given the current URL pathname, return the legacy
 * screen-name string. Used by App.jsx so gates that branch on
 * `screen` keep working during the G1 window (e.g. the mascot-pick
 * gate skips itself when `screen === "session"`). */
export function pathToScreen(pathname) {
  return PATH_TO_SCREEN[pathname] ?? "home";
}
